import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import {
  orderTrackingMainContainerStyle,
  orderNotFoundContainerStyle,
} from "./OrderTrackingStyles";
import { orderSummaryStages } from "../../constants/orderSummary";
import { OrderDetails as OrderDetailsProps } from "../../dto/orderSummaryTypes";
import ConfirmationDialog from "../../common/confirmationDialog/ConfirmationDialog";
import Toast from "../../common/Toast";
import moment from "moment";
import { sendCustomEvents } from "../../ga";
import {
  fetchOrderDetails,
  editOrderAPI,
  downloadInvoiceAPI,
} from "../../api/orderFinalAPI";
import PageHeader from "../../common/PageHeader";
import CustomerOrderLeft from "./CustomerOrderLeft";
import OrderSummaryRight from "./orderSummaryRight";
import Colors from "../../theme/Colours";

const OrderTracking = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const orderId = location.pathname.split("/").pop();

  const [orderDetails, setOrderDetails] = useState<OrderDetailsProps>();
  const [isOrderCancelled, setIsOrderCancelled] = useState(false);
  const [currentStage, setCurrentStage] = useState(0);
  const [showDownloadInvoiceError, setShowDownloadInvoiceError] =
    useState(false);

  const [error, setError] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const orgId = localStorage.getItem("orgId");
  const userRole = localStorage.getItem("userRole");

  const getOrderDetails = async () => {
    if (!orderId) return;
    setIsLoading(true);
    try {
      const data = await fetchOrderDetails(orderId);
      if (data) {
        console.log("Order Details Data:", data.data);
        console.log("Payment Status:", data.data.payment?.status);
        console.log("Order Status:", data.data.status);
        setOrderDetails(data.data);
        const stageIndex = orderSummaryStages.findIndex(
          (stage) => stage.title === data.data.status
        );
        data?.data?.eta && moment(data.data.eta);
        setCurrentStage(stageIndex);

        if (data.data.status.toUpperCase() === "CANCELLED") {
          setIsOrderCancelled(true);
        }
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!orderDetails) getOrderDetails();
  }, []);

  const [showCancelOrder, setShowCancelOrder] = useState(false);

  const cancelOrder = async () => {
    setShowCancelOrder(false);
    try {
      const response = await editOrderAPI(orderId!, {
        newStatus: "CANCELLED",
      });

      if (response?.status === 201) {
        if (response.data.data?.order?.orderStatus === "CANCELLED") {
          setIsOrderCancelled(true);
          setToastMessage("Order has been Cancelled");
          setError(false);
          setShowToast(true);
          setTimeout(() => {
            if (userRole === "maltora-seller") {
              navigate(`/maltora-seller/orders`);
            } else if (userRole === "maltora-customer") {
              navigate(`/maltora-customer/my-orders`);
            }
          }, 1000);
        }
      }
    } catch (error) {
      console.error(error);
    }
    setShowToast(true);
  };

  const downloadInvoice = async () => {
    try {
      const response = await downloadInvoiceAPI(orderId!);
      if (response.data.data) {
        const link = document.createElement("a");
        link.href = response.data.data;
        link.download = `invoice-${orderId}.pdf`;
        link.target = "_blank";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        setShowDownloadInvoiceError(true);
      }
    } catch (error) {
      console.error(error);
      setShowDownloadInvoiceError(true);
    }
  };

  const makePayment = () => {
    // Navigate to the payment page for this order
    navigate(`/maltora-customer/my-orders/${orderId}/payment`);
  };
  return (
    <>
      <ConfirmationDialog
        buttonAction={cancelOrder}
        buttonTitle="Yes, I Am Sure"
        show={showCancelOrder}
        setShow={setShowCancelOrder}
        title="Are you sure you want to cancel?"
      />

      <ConfirmationDialog
        buttonAction={() => {
          downloadInvoice();
          setShowDownloadInvoiceError(false);
        }}
        buttonTitle="Retry"
        error={true}
        show={showDownloadInvoiceError}
        setShow={setShowDownloadInvoiceError}
        title="Invoice not found"
      />

      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
      />

      {orderDetails ? (
        <>
          <PageHeader title="Order Tracking" />
          <Box sx={orderTrackingMainContainerStyle}>
            {/* LEFT rail */}
            <CustomerOrderLeft
              orderId={orderDetails.id}
              statusHistory={orderDetails.statusHistory ?? []}
              shippingAddress={orderDetails.shippingAddress}
              billingAddress={orderDetails.billingAddress}
              currentStage={currentStage}
              canDownload={
                !!orderDetails.isInvoiceGenerated && !isOrderCancelled
              }
              onDownloadInvoice={downloadInvoice}
              isOrderCancelled={isOrderCancelled}
              orderStatus={orderDetails.status}
              paymentStatus={orderDetails.payment?.status}
              onMakePayment={makePayment}
            />

            {/* RIGHT rail */}
            <OrderSummaryRight
              orders={orderDetails.orders}
              payment={orderDetails.payment}
              notes={orderDetails.customerNote}
              isCancellationAllowed={currentStage < 3}
              isOrderCancelled={isOrderCancelled}
              onCancelRequest={() => {
                sendCustomEvents(
                  "Seller",
                  "Clicked Cancel Order",
                  "Cancel Order in Order summary"
                );
                setShowCancelOrder(true);
              }}
            />
          </Box>
          {isOrderCancelled && (
            <Box
              sx={{
                backgroundColor: Colors.redBackground,
                border: `1px solid ${Colors.redText}`,
                borderRadius: 1,
                p: 2,
                mt: 2,
                textAlign: "center",
                mx: "auto",
                maxWidth: 1200,
              }}
            >
              <Typography variant="h6" color={Colors.redText} fontWeight="bold">
                This order has been cancelled
              </Typography>
            </Box>
          )}
        </>
      ) : isLoading ? null : (
        <Box sx={orderNotFoundContainerStyle}>
          <Typography>{`Order #${orderId} not found`}</Typography>
        </Box>
      )}

      <Toast
        showToast={showToast}
        setShowToast={setShowToast}
        toastMessage={toastMessage}
        error={error}
        data-testid={error ? "toast-error" : "toast-success"}
      />
    </>
  );
};

export default OrderTracking;
